import React, { useState, useEffect } from 'react';
import { API_BASE } from '../services/api';

export default function StackSelector({ value = 'react-node', onChange }) {
  const [stacks, setStacks] = useState([]);
  const [popular, setPopular] = useState([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchStacks();
  }, []);

  const fetchStacks = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/stacks`);
      if (res.ok) {
        const data = await res.json();
        setStacks(data.stacks || []);
        setPopular(data.popular || []);
      }
    } catch (e) {
      // Fallback stacks if API fails
      setPopular([
        { id: 'react-node', label: 'React + Node.js', frontend: 'react', backend: 'node-express', description: 'Most popular full-stack', popular: true },
        { id: 'react-python', label: 'React + FastAPI', frontend: 'react', backend: 'python-fastapi', description: 'Python backend', popular: true },
        { id: 'react-only', label: 'React Only', frontend: 'react', backend: null, description: 'Frontend only', popular: true },
      ]);
    }
  };

  const selected = value || 'react-node';
  const INITIAL_COUNT = 4;
  const orderedStacks = (stacks.length > 0 ? stacks : popular)
    .slice()
    .sort((a, b) => {
      if (a.id === 'react-node') return -1;
      if (b.id === 'react-node') return 1;
      return (b.popular ? 1 : 0) - (a.popular ? 1 : 0);
    });
  let visibleStacks = showAll ? orderedStacks : orderedStacks.slice(0, INITIAL_COUNT);
  if (!showAll && selected && !visibleStacks.some(s => s.id === selected)) {
    const sel = orderedStacks.find(s => s.id === selected);
    if (sel) visibleStacks = [...visibleStacks.slice(0, INITIAL_COUNT - 1), sel];
  }

  return (
    <div style={s.container}>
      {/* Popular Stacks */}
      <div style={s.grid}>
        {visibleStacks.map(stack => (
          <div
            key={stack.id}
            onClick={() => onChange(stack.id)}
            style={{
              ...s.card,
              ...(selected === stack.id ? s.cardSelected : {}),
            }}
          >
            <div style={s.cardHeader}>
              <span style={s.cardTitle}>{stack.label}</span>
              {stack.popular && <span style={s.popularBadge}>⭐ Popular</span>}
            </div>
            <p style={s.cardDesc}>{stack.description}</p>
            <div style={s.badges}>
              {stack.frontend && (
                <span style={s.feBadge}>{_getIcon(stack.frontend)} {stack.frontend}</span>
              )}
              {stack.backend && (
                <span style={s.beBadge}>{_getIcon(stack.backend)} {stack.backend}</span>
              )}
              {!stack.frontend && !stack.backend && (
                <span style={s.feBadge}>📦 {stack.id}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Show All Toggle */}
      {!showAll && orderedStacks.length > INITIAL_COUNT && (
        <button onClick={() => setShowAll(true)} style={s.showAllBtn}>
          Show all {orderedStacks.length} tech stacks ↓
        </button>
      )}
      {showAll && (
        <button onClick={() => setShowAll(false)} style={s.showAllBtn}>
          Show less ↑
        </button>
      )}
    </div>
  );
}

function _getIcon(tech) {
  const icons = {
    'react': '⚛️', 'react-native': '📱', 'angular': '🅰️', 'vue': '💚',
    'nextjs': '▲', 'node-express': '🟢', 'python-fastapi': '🐍',
    'java-spring': '☕', 'dotnet': '🟣', 'node': '🟢', 'python': '🐍',
  };
  return icons[tech] || '📦';
}

const s = {
  container: { marginBottom: 16 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 },
  card: {
    padding: '12px 14px', background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
    cursor: 'pointer', transition: 'all 0.15s',
  },
  cardSelected: {
    borderColor: '#6366f1', background: 'rgba(99,102,241,0.08)',
    boxShadow: '0 0 0 1px #6366f1',
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  cardTitle: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' },
  popularBadge: { fontSize: 9, color: '#f59e0b', fontWeight: 600 },
  cardDesc: { fontSize: 11, color: '#64748b', margin: '4px 0 8px' },
  badges: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  feBadge: { fontSize: 10, padding: '2px 6px', background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', borderRadius: 4, fontWeight: 600 },
  beBadge: { fontSize: 10, padding: '2px 6px', background: 'rgba(34,197,94,0.12)', color: '#86efac', borderRadius: 4, fontWeight: 600 },
  showAllBtn: { marginTop: 10, padding: '6px 14px', background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#94a3b8', fontSize: 12, cursor: 'pointer', width: '100%' },
};
